import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Mail, Lock, Eye, EyeOff,
} from "lucide-react";
import { useAuth } from "../../data/authStore";
import { apiError } from "../../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow OS — Login (iter-296 calm refinement)

   A single, centred auth surface on a pure-white background.
   Inspired by Linear, Stripe and Attio — infrastructure-grade,
   never decorative. The split-screen, Google CTA, Remember-me
   toggle and editorial illustrations from iter-257 are retired
   per the operator brief. Every auth wire is preserved verbatim:
     • `useAuth().login`
     • Session-expired routing (`?reason=expired&next=…`)
     • `apiError(...)` + `toast.error(...)`
     • `submitting` button state
     • Canonical testids (`login-page`, `login-form`, `login-email`,
       `login-password`, `login-toggle-password`, `login-forgot`,
       `login-error`, `login-submit`).
*/

const BRAND_NAVY = "#08112A";       // solid navy wordmark
const PRIMARY_PURPLE = "#5B35FF";   // premium purple CTA

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  // Calm "Session expired" routing — preserved verbatim.  Post-login
  // destination: Command Centre (`/today`) is the operator's daily
  // priority landing page.  `next=` deep-link OR `location.state.from`
  // (RequireAuth's remembered target) still override so a user who
  // clicked into a specific Deal Room before auth challenges still
  // lands where they were headed.
  const params = new URLSearchParams(location.search);
  const expired = params.get("reason") === "expired";
  const nextPath = params.get("next");
  const redirectTo = nextPath
    ? decodeURIComponent(nextPath)
    : (location.state?.from || "/today");

  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [showPwd, setShowPwd]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(
    expired ? "Your session has expired. Please sign in again." : "",
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Enter your email and password");
      return;
    }
    setSubmitting(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${user.full_name?.split(" ")[0] || "there"}.`);
      nav(redirectTo, { replace: true });
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error("Sign in failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center bg-white px-6 font-sans"
      style={{ fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px] pt-14 lg:pt-[12vh] pb-10" data-testid="login-left">
        <Link to="/" className="flex justify-center select-none" data-testid="login-brand" aria-label="ChainFlow home">
          {/* Original asset, unscaled ratio; its PNG has ~2.7px transparent edge, hence mt-[29px] below = 32px visible gap. */}
          <img
            src="/chainflow-logo.png"
            alt="ChainFlow"
            className="block w-[190px] sm:w-[220px] h-auto"
            data-testid="login-brand-logo"
          />
        </Link>

        <div className="mt-[29px] text-center">
          <h1
            className="font-bold tracking-[-0.03em] leading-[1.1] text-[29px] sm:text-[36px]"
            style={{ color: BRAND_NAVY }}
            data-testid="auth-title"
          >
            Welcome back
          </h1>
          <p className="mt-2 text-[15px] text-slate-500" data-testid="login-subtitle">
            Access your transaction workspace.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-8" data-testid="login-form" noValidate>
          <LoginField
            id="login-email-input"
            label="Email address"
            icon={Mail}
            testid="login-email"
            inputProps={{
              type: "email", name: "email", value: email, autoComplete: "username email",
              inputMode: "email", placeholder: "you@example.com",
              onChange: (e) => setEmail(e.target.value),
            }}
          />
          <LoginField
            id="login-password-input"
            label="Password"
            icon={Lock}
            testid="login-password"
            className="mt-6"
            labelAside={(
              <Link to="/forgot-password" className="text-[13px] font-semibold rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
                style={{ color: PRIMARY_PURPLE }} data-testid="login-forgot">
                Forgot password?
              </Link>
            )}
            inputProps={{
              type: showPwd ? "text" : "password", name: "password", value: password,
              autoComplete: "current-password", placeholder: "Enter your password",
              onChange: (e) => setPassword(e.target.value),
            }}
            suffix={(
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="h-9 w-9 -mr-1.5 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
                data-testid="login-toggle-password"
                aria-label={showPwd ? "Hide password" : "Show password"}
                aria-pressed={showPwd}
              >
                {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            )}
          />

          {error && (
            <p className="mt-4 text-[13px] text-rose-700 font-medium" role="alert" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl text-white text-[15px] font-semibold transition-[background-color,box-shadow,opacity] hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2"
            style={{ backgroundColor: PRIMARY_PURPLE, boxShadow: "0 1px 2px rgba(91,53,255,0.16), 0 6px 16px -8px rgba(91,53,255,0.45)" }}
            data-testid="login-submit"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Signing in…</>
            ) : (
              <>Sign in <ArrowRight className="h-4 w-4" aria-hidden /></>
            )}
          </button>

          <p className="mt-5 text-center text-[14px] text-slate-500">
            New to ChainFlow?{" "}
            <Link to="/signup" className="font-semibold rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
              style={{ color: PRIMARY_PURPLE }} data-testid="login-register">
              Create an account
            </Link>
          </p>
        </form>

        <footer className="mt-12 text-center" data-testid="login-footer">
          <div className="text-[13.5px] font-medium text-slate-800">Built for UK property transactions</div>
          <div className="mt-1 text-[12.5px] text-slate-500">Clear progress · Connected teams · Confident next steps</div>
        </footer>
      </div>
    </div>
  );
}

function LoginField({ id, label, icon: Icon, testid, inputProps, suffix, labelAside, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="text-[14px] font-medium text-slate-800">{label}</label>
        {labelAside}
      </div>
      <div className="flex items-center gap-2.5 h-12 px-3.5 rounded-xl bg-white border border-slate-300 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[border-color,box-shadow] focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200">
        <Icon size={17} className="shrink-0 text-slate-500" aria-hidden />
        <input
          id={id}
          className="flex-1 min-w-0 h-full bg-transparent text-[15px] text-slate-900 placeholder:text-slate-500 outline-none"
          data-testid={testid}
          {...inputProps}
        />
        {suffix}
      </div>
    </div>
  );
}
